import { api } from '@/services/api';

// admin_api is the typed client for all /api/v1/admin/* endpoints.
// Tokens ride on the same axios instance as the user taskpane — the
// admin's JWT just carries role='admin' in its claims, and the
// RequireRole middleware enforces it server-side.

// --- Auth ----------------------------------------------------------------

export interface AdminLoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: { id: string; email: string; role: string };
}

export async function adminLogin(email: string, password: string): Promise<AdminLoginResponse> {
  const { data } = await api.post<AdminLoginResponse>('/api/v1/admin/auth/login', { email, password });
  return data;
}

// --- Models catalog ------------------------------------------------------

export interface AIModel {
  id: string;
  provider: 'anthropic' | 'deepseek';
  display_name: string;
  exact_model_id: string;
  description: string;
  context_window: number;
  max_output_tokens: number;
  supports_tools: boolean;
  supports_vision: boolean;
  supports_thinking: boolean;
  supports_caching: boolean;
  // provider_options carries per-model quirks the backend reads at
  // chat time (e.g. Anthropic's thinking_shape, OpenAI's
  // reasoning_token_floor). See migration 000006 for the convention.
  provider_options?: Record<string, unknown>;
  created_at: string;
}

export async function listModels(provider?: string): Promise<AIModel[]> {
  const { data } = await api.get<{ models: AIModel[] }>('/api/v1/admin/models', {
    params: provider ? { provider } : undefined,
  });
  return data.models ?? [];
}

export async function createModel(p: {
  provider: string;
  display_name: string;
  exact_model_id: string;
  description?: string;
  context_window?: number;
  max_output_tokens?: number;
  supports_tools?: boolean;
  supports_vision?: boolean;
  supports_thinking?: boolean;
  supports_caching?: boolean;
  provider_options?: Record<string, unknown>;
}): Promise<AIModel> {
  const { data } = await api.post<AIModel>('/api/v1/admin/models', p);
  return data;
}

export async function deleteModel(id: string): Promise<void> {
  await api.delete(`/api/v1/admin/models/${id}`);
}

// --- Templates -----------------------------------------------------------

export interface AITemplate {
  id: string;
  name: string;
  provider: 'anthropic' | 'deepseek';                 // reasoning-slot provider
  exact_model_id: string;          // main agent model (Reasoning recommended)
  agentic_provider?: 'anthropic' | 'deepseek';        // agentic-slot provider (defaults to reasoning provider)
  agentic_model_id?: string;       // optional cheap model for classifier
  daily_token_limit: number;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function listTemplates(): Promise<AITemplate[]> {
  const { data } = await api.get<{ templates: AITemplate[] }>('/api/v1/admin/templates');
  return data.templates ?? [];
}

export async function createTemplate(p: {
  name: string; provider: string; exact_model_id: string;
  agentic_provider?: 'anthropic' | 'deepseek'; agentic_model_id?: string;
  api_key: string; agentic_api_key?: string;
  daily_token_limit: number; description?: string;
}): Promise<AITemplate> {
  const { data } = await api.post<AITemplate>('/api/v1/admin/templates', p);
  return data;
}

export async function updateTemplate(id: string, p: {
  name: string; provider: string; exact_model_id: string;
  agentic_provider?: 'anthropic' | 'deepseek'; agentic_model_id?: string;
  api_key?: string; agentic_api_key?: string;
  daily_token_limit: number; description?: string;
}): Promise<void> {
  await api.patch(`/api/v1/admin/templates/${id}`, p);
}

export async function deleteTemplate(id: string): Promise<void> {
  await api.delete(`/api/v1/admin/templates/${id}`);
}

export async function activateTemplate(id: string): Promise<void> {
  await api.post(`/api/v1/admin/templates/${id}/activate`);
}

export async function testKey(p: {
  provider: string; exact_model_id: string; api_key: string;
}): Promise<void> {
  await api.post('/api/v1/admin/templates/test-key', p);
}

// --- Users ---------------------------------------------------------------

export interface AdminUser {
  id: string;
  email: string;
  role: 'user' | 'admin';
  is_active: boolean;
  credits: number;
  subscription_start: string | null;
  subscription_end: string | null;
  last_login_at: string | null;
  created_at: string;
}

export async function listUsers(q = '', limit = 100, offset = 0): Promise<AdminUser[]> {
  const { data } = await api.get<{ users: AdminUser[] }>('/api/v1/admin/users', {
    params: { q, limit, offset },
  });
  return data.users ?? [];
}

export async function createUser(p: {
  email: string; password: string; role?: 'user' | 'admin';
  is_active?: boolean; subscription_start?: string; subscription_end?: string;
  credits?: number;
}): Promise<AdminUser> {
  const { data } = await api.post<AdminUser>('/api/v1/admin/users', p);
  return data;
}

export async function updateUser(id: string, p: {
  is_active?: boolean; new_password?: string;
  subscription_start?: string; subscription_end?: string;
  credits?: number;
}): Promise<void> {
  await api.patch(`/api/v1/admin/users/${id}`, p);
}

export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/api/v1/admin/users/${id}`);
}

export interface ImportResult {
  created: number;
  skipped: number;
  errors: { row: number; reason: string }[];
}

export async function importUsers(file: File): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<ImportResult>('/api/v1/admin/users/import', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300_000,
  });
  return data;
}

// --- User stats ----------------------------------------------------------

export interface AdminUserStats {
  total: number;
  active: number;
  active_7d: number;
  active_30d: number;
  today_added: number;
  never_logged: number;
  // Live activity (Redis-backed). Counts users with any authenticated
  // API request in the window — closer to "currently using the addin"
  // than the login-based active_7d/30d counters.
  live_5min: number;
  live_activity_1h: number;
  live_activity_24h: number;
}

export async function getUserStats(): Promise<AdminUserStats> {
  const { data } = await api.get<AdminUserStats>('/api/v1/admin/users/stats');
  return data;
}

// --- Server health (OS metrics) ------------------------------------------

export interface ServerHealth {
  cpu_usage_pct: number;
  cpu_cores: number;
  cpu_load_1: number;
  cpu_load_5: number;
  cpu_load_15: number;
  mem_total_bytes: number;
  mem_used_bytes: number;
  mem_free_bytes: number;
  mem_usage_pct: number;
  disk_total_bytes: number;
  disk_used_bytes: number;
  disk_free_bytes: number;
  disk_usage_pct: number;
  disk_mountpoint: string;
  hostname: string;
  os: string;
  platform: string;
  uptime_seconds: number;
  go_version: string;
  now: string;
}

export async function getServerHealth(): Promise<ServerHealth> {
  const { data } = await api.get<ServerHealth>('/api/v1/admin/server/stats');
  return data;
}

// --- Chunked import (production) -----------------------------------------
//
// importUsersChunked parses the workbook in the browser, splits it into
// fixed-size batches, and POSTs each batch to the existing /import
// endpoint. Survives nginx/proxy timeouts that single-shot uploads hit on
// large files (600+ rows). Backend already skips duplicate emails via the
// users_email_key UNIQUE constraint, so already-imported users count as
// "skipped" and never get processed twice.
//
// onProgress fires after each batch with the running tally so the UI can
// render a live progress bar without polling.

import * as XLSX from 'xlsx';

export interface ImportProgress {
  batchIndex: number;
  batchCount: number;
  processedRows: number;
  totalRows: number;
  created: number;
  skipped: number;
  errors: { row: number; reason: string }[];
}

export interface ImportFinalResult {
  totalRows: number;
  created: number;
  skipped: number;
  errors: { row: number; reason: string }[];
  durationMs: number;
}

const IMPORT_BATCH_SIZE = 50;

/**
 * Reads an XLSX file in the browser, splits its data rows into chunks of
 * IMPORT_BATCH_SIZE, and uploads each chunk as a smaller XLSX. Reports
 * progress after every batch.
 *
 * Row numbering: backend reports 1-based row numbers WITHIN its batch.
 * We translate those back to the source-sheet row number so the admin
 * can map errors to their original file.
 */
export async function importUsersChunked(
  file: File,
  onProgress: (p: ImportProgress) => void,
  opts: { batchSize?: number; signal?: AbortSignal } = {},
): Promise<ImportFinalResult> {
  const batchSize = opts.batchSize ?? IMPORT_BATCH_SIZE;
  const started = performance.now();

  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('Workbook has no sheets');
  const sheet = wb.Sheets[sheetName];
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  if (rows.length < 2) throw new Error('Sheet must have a header row + at least one data row');

  const header = rows[0];
  const dataRows = rows.slice(1).filter((r) => Array.isArray(r) && r.some((c) => String(c).trim() !== ''));
  const totalRows = dataRows.length;
  const batchCount = Math.ceil(totalRows / batchSize);

  let created = 0;
  let skipped = 0;
  const errors: { row: number; reason: string }[] = [];

  for (let b = 0; b < batchCount; b++) {
    if (opts.signal?.aborted) throw new Error('Import cancelled');
    const start = b * batchSize;
    const end = Math.min(start + batchSize, totalRows);
    const slice = dataRows.slice(start, end);

    // Build a mini XLSX containing the header + this batch's rows.
    const miniWb = XLSX.utils.book_new();
    const miniSheet = XLSX.utils.aoa_to_sheet([header, ...slice]);
    XLSX.utils.book_append_sheet(miniWb, miniSheet, 'Users');
    const miniBuf: ArrayBuffer = XLSX.write(miniWb, { type: 'array', bookType: 'xlsx' });
    const miniFile = new File([miniBuf], `import-batch-${b + 1}.xlsx`, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    try {
      const res = await importUsers(miniFile);
      created += res.created;
      skipped += res.skipped;
      // Translate batch-local row numbers back to source-sheet rows.
      // Header is row 1; batch row index i (1-based) maps to source row
      // (start + i + 1) because the batch already strips earlier rows.
      for (const e of res.errors ?? []) {
        const sourceRow = start + e.row; // e.row is batch-local 1-based for data; +start offsets
        errors.push({ row: sourceRow + 1, reason: e.reason });
      }
    } catch (err: any) {
      // Whole batch failed (network / timeout) — record one error per
      // row in the batch so the admin can identify a retry slice.
      const reason = err?.message || 'batch failed';
      for (let i = 0; i < slice.length; i++) {
        errors.push({ row: start + i + 2, reason: `batch ${b + 1}: ${reason}` });
      }
    }

    onProgress({
      batchIndex: b + 1,
      batchCount,
      processedRows: end,
      totalRows,
      created,
      skipped,
      errors,
    });
  }

  return {
    totalRows,
    created,
    skipped,
    errors,
    durationMs: Math.round(performance.now() - started),
  };
}

export async function exportUsers(): Promise<Blob> {
  const resp = await api.get('/api/v1/admin/users/export', { responseType: 'blob' });
  return resp.data as Blob;
}

export async function downloadImportTemplate(): Promise<Blob> {
  const resp = await api.get('/api/v1/admin/users/template', { responseType: 'blob' });
  return resp.data as Blob;
}

// --- Email queue ---------------------------------------------------------

export interface EmailQueueStatus {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  in_progress: boolean;
  started_at?: string;
  eta_seconds: number;
  throughput_per_sec: number;
}

export async function getEmailQueueStatus(): Promise<EmailQueueStatus> {
  const { data } = await api.get<EmailQueueStatus>('/api/v1/admin/email-queue/status');
  return data;
}

// --- Enrollment mode (paid/free toggle) ---------------------------------

export interface EnrollmentMode {
  mode: 'paid' | 'free';
  free_mode_expires?: string;
  // Per-signup credit grant for free mode. Backend always populates it
  // (falls back to the 10,000 default when unset).
  free_mode_credits?: number;
}

export async function getEnrollmentModeAdmin(): Promise<EnrollmentMode> {
  const { data } = await api.get<EnrollmentMode>('/api/v1/admin/enrollment/mode');
  return data;
}

// setEnrollmentModeAdmin writes the mode. credits is only meaningful when
// switching to 'free' — pass it to override the per-signup grant; omit to
// keep the stored value. Switching to 'paid' never touches credits.
export async function setEnrollmentModeAdmin(
  mode: 'paid' | 'free',
  expiresAt?: string | null,
  credits?: number,
): Promise<void> {
  await api.put('/api/v1/admin/enrollment/mode', {
    mode,
    expires_at: expiresAt ?? null,
    ...(credits != null ? { credits } : {}),
  });
}

// --- Dashboard -----------------------------------------------------------

export interface DashboardSummary {
  total_users: number;
  active_users: number;
  total_tokens: number;
  // total_cost_usd is deprecated — cost tracking removed product-wide.
  // Field kept optional so older backend versions still parse cleanly.
  total_cost_usd?: number;
  total_requests: number;
}

export async function dashboardSummary(days = 30): Promise<DashboardSummary> {
  const { data } = await api.get<{ summary: DashboardSummary }>(
    '/api/v1/admin/dashboard/summary', { params: { days } },
  );
  return data.summary;
}

export interface TimeSeriesPoint {
  date: string;
  tokens: number;
  cost_usd?: number; // deprecated — see DashboardSummary
  requests: number;
}

export async function dashboardTimeSeries(days = 30): Promise<TimeSeriesPoint[]> {
  const { data } = await api.get<{ points: TimeSeriesPoint[] }>(
    '/api/v1/admin/dashboard/timeseries', { params: { days } },
  );
  return data.points ?? [];
}

export interface ByModelPoint {
  provider: string;
  model: string;
  tokens: number;
  cost_usd?: number; // deprecated
  requests: number;
}

export async function dashboardByModel(days = 30): Promise<ByModelPoint[]> {
  const { data } = await api.get<{ points: ByModelPoint[] }>(
    '/api/v1/admin/dashboard/by-model', { params: { days } },
  );
  return data.points ?? [];
}

export interface ByUserPoint {
  user_id: string;
  email: string;
  tokens: number;
  cost_usd?: number; // deprecated
  requests: number;
}

export async function dashboardByUser(days = 30, limit = 50): Promise<ByUserPoint[]> {
  const { data } = await api.get<{ points: ByUserPoint[] }>(
    '/api/v1/admin/dashboard/by-user', { params: { days, limit } },
  );
  return data.points ?? [];
}

// --- Payments ------------------------------------------------------------
//
// Read-only reporting over the purchases ledger. The status dropdown speaks
// finance terms (captured/pending); the backend maps those onto the DB
// status values (paid/created). Date range is interpreted server-side as
// IST calendar days.

// Pending is intentionally absent — the admin Payments view is live-only
// and never surfaces pending (abandoned) orders.
export type PaymentStatusFilter = 'all' | 'captured' | 'failed' | 'refunded';

export interface PaymentFilterParams {
  from?: string;  // YYYY-MM-DD (inclusive, IST)
  to?: string;    // YYYY-MM-DD (inclusive, IST)
  status?: PaymentStatusFilter;
}

export interface PaymentRow {
  id: string;
  created_at: string;
  paid_at: string | null;
  email: string;
  phone: string | null;
  pack_name: string;
  pack_code: string;
  amount_inr: number;
  credits_purchased: number;
  status: 'created' | 'paid' | 'failed' | 'refunded';
  razorpay_order_id: string;
  razorpay_payment_id: string | null;
  invoice_number: string | null;
  has_receipt: boolean;
}

export interface PaymentSummary {
  captured_count: number;
  revenue_inr: number;
  pending_count: number;
  failed_count: number;
  refunded_count: number;
  total_count: number;
}

// filterToParams drops empty/`all` values so the query string stays clean
// and the backend applies no bound for omitted fields.
function filterToParams(f: PaymentFilterParams): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.from) p.from = f.from;
  if (f.to) p.to = f.to;
  if (f.status && f.status !== 'all') p.status = f.status;
  return p;
}

export async function listPayments(
  f: PaymentFilterParams = {}, limit = 100, offset = 0,
): Promise<PaymentRow[]> {
  const { data } = await api.get<{ payments: PaymentRow[] }>('/api/v1/admin/payments', {
    params: { ...filterToParams(f), limit, offset },
  });
  return data.payments ?? [];
}

export async function paymentsSummary(f: PaymentFilterParams = {}): Promise<PaymentSummary> {
  const { data } = await api.get<PaymentSummary>('/api/v1/admin/payments/summary', {
    params: filterToParams(f),
  });
  return data;
}

export async function exportPayments(f: PaymentFilterParams = {}): Promise<Blob> {
  const resp = await api.get('/api/v1/admin/payments/export', {
    params: filterToParams(f),
    responseType: 'blob',
  });
  return resp.data as Blob;
}

// downloadReceipt fetches the regenerated GST invoice PDF for one captured
// payment. Returns the blob + the server-suggested filename (parsed from
// Content-Disposition), falling back to a receipt-<id> name.
export async function downloadReceipt(id: string): Promise<{ blob: Blob; filename: string }> {
  const resp = await api.get(`/api/v1/admin/payments/${id}/receipt`, { responseType: 'blob' });
  const cd = String(resp.headers?.['content-disposition'] ?? '');
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = m?.[1] ?? `receipt-${id}.pdf`;
  return { blob: resp.data as Blob, filename };
}
